import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  TextField,
  Popover,
  Typography,
  IconButton,
  Button,
  Paper,
  useTheme,
  alpha,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { SelectionStep } from '@/entities/ui/types';

dayjs.extend(customParseFormat);

interface DateRangePickerProps {
  startDate: string | null;
  endDate: string | null;
  onDateRangeChange: (startDate: string | null, endDate: string | null) => void;
  includeTime?: boolean;
  error?: boolean;
  helperText?: string;
  label?: string;
  disabled?: boolean;
  minDate?: string;
  maxDate?: string;
  availableDates?: string[]; // New prop to restrict active dates
  placeholder?: string;
  lockedStartDate?: string | null;
  size?: 'small' | 'medium';
  variant?: 'outlined' | 'filled' | 'standard';
  fullWidth?: boolean;
  required?: boolean;
  className?: string;
  sx?: any;
  popoverProps?: {
    anchorOrigin?: any;
    transformOrigin?: any;
    maxWidth?: number;
    minWidth?: number;
  };
  calendarProps?: {
    showNavigation?: boolean;
    showTodayHighlight?: boolean;
    allowSingleDate?: boolean;
    dateFormat?: string;
    enforceMinDays?: boolean;
    minDays?: number;
    singleMonth?: boolean;
  };
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  includeTime = false,
  onDateRangeChange,
  error = false,
  helperText,
  label = 'Please Enter Start & End Dates',
  disabled = false,
  minDate,
  maxDate,
  availableDates,
  placeholder = 'Select date range',
  lockedStartDate,
  size = 'medium',
  variant = 'outlined',
  fullWidth = true,
  required = false,
  className,
  sx,
  popoverProps = {},
  calendarProps = {},
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [tempStartDate, setTempStartDate] = useState<Dayjs | null>(
    startDate ? dayjs(startDate) : null
  );
  const [tempEndDate, setTempEndDate] = useState<Dayjs | null>(
    endDate ? dayjs(endDate) : null
  );
  const [currentMonth, setCurrentMonth] = useState<Dayjs>(dayjs());

  const [selectionStep, setSelectionStep] =
    useState<SelectionStep>('startDate');
  const [tempStartTime, setTempStartTime] = useState(dayjs().format('HH:mm'));
  const [tempEndTime, setTempEndTime] = useState(dayjs().format('23:59'));

  const resetTempTimes = useCallback(() => {
    setTempStartTime(
      startDate ? dayjs(startDate).format('HH:mm') : dayjs().format('HH:mm')
    );

    setTempEndTime(endDate ? dayjs(endDate).format('HH:mm') : '23:59');
  }, [startDate, endDate]);

  const open = Boolean(anchorEl);
  const showCalendar =
    !includeTime ||
    selectionStep === 'startDate' ||
    selectionStep === 'endDate' ||
    selectionStep === 'done';

  const goToStartStep = useCallback(() => {
    setSelectionStep(includeTime ? 'startTime' : 'endDate');
  }, [includeTime]);

  const goToEndStep = useCallback(() => {
    setSelectionStep(includeTime ? 'endTime' : 'done');
  }, [includeTime]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!disabled) {
        setAnchorEl(event.currentTarget);
        resetTempTimes();
        setSelectionStep('startDate');

        if (startDate && endDate) {
          setCurrentMonth(dayjs(startDate));

          if (includeTime) {
            setSelectionStep('done');
          }
        }
      }
    },
    [disabled, startDate, endDate, resetTempTimes, includeTime]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    resetTempTimes();
  }, [resetTempTimes]);

  // Set default calendar props
  const {
    showNavigation = true,
    showTodayHighlight = true,
    allowSingleDate = false,
    dateFormat = 'MMM DD, YYYY',
    enforceMinDays = false,
    minDays = 30,
    singleMonth = false,
  } = calendarProps;

  const handleApply = useCallback(() => {
    const startDateTime = tempStartDate
      ? includeTime
        ? `${tempStartDate.format('YYYY-MM-DD')}T${tempStartTime}:00`
        : tempStartDate.format('YYYY-MM-DD')
      : null;

    const endDateTime = tempEndDate
      ? includeTime
        ? `${tempEndDate.format('YYYY-MM-DD')}T${tempEndTime}:00`
        : tempEndDate.format('YYYY-MM-DD')
      : null;

    onDateRangeChange(startDateTime, endDateTime);
    setAnchorEl(null);
  }, [
    tempStartDate,
    tempEndDate,
    tempStartTime,
    tempEndTime,
    includeTime,
    onDateRangeChange,
  ]);

  // Check if Apply button should be disabled
  const isApplyDisabled = useCallback(() => {
    if (selectionStep !== 'done') return true;

    if (allowSingleDate) {
      return !tempStartDate;
    }

    return !tempStartDate || !tempEndDate;
  }, [tempStartDate, tempEndDate, allowSingleDate, selectionStep]);

  const formatDateRange = useCallback(() => {
    if (!startDate && !endDate) return '';

    if (!includeTime) {
      if (startDate && !endDate) {
        return dayjs(startDate).format(dateFormat);
      }

      if (!startDate && endDate) {
        return dayjs(endDate).format(dateFormat);
      }

      return `${dayjs(startDate).format(dateFormat)} - ${dayjs(endDate).format(dateFormat)}`;
    }

    if (startDate && !endDate) {
      return `${dayjs(startDate).format(dateFormat)} ${dayjs(startDate).format('HH:mm')}`;
    }

    if (!startDate && endDate) {
      return `${dayjs(endDate).format(dateFormat)} ${dayjs(endDate).format('HH:mm')}`;
    }

    return `${dayjs(startDate).format(dateFormat)} ${dayjs(startDate).format('hh:mm A')} - ${dayjs(endDate).format(dateFormat)} ${dayjs(endDate).format('hh:mm A')}`;
  }, [startDate, endDate, dateFormat, includeTime]);

  // Generate calendar data for current and next month
  const calendarData = useMemo(() => {
    const firstMonth = currentMonth;
    const secondMonth = currentMonth.add(1, 'month');

    const generateMonthData = (month: Dayjs) => {
      const startOfMonth = month.startOf('month');
      const endOfMonth = month.endOf('month');
      const startOfWeek = startOfMonth.startOf('week');
      const endOfWeek = endOfMonth.endOf('week');

      const days = [];
      let current = startOfWeek;

      while (current.isBefore(endOfWeek) || current.isSame(endOfWeek, 'day')) {
        days.push({
          date: current,
          isCurrentMonth: current.isSame(month, 'month'),
          isToday: current.isSame(dayjs(), 'day'),
        });
        current = current.add(1, 'day');
      }

      return days;
    };

    return {
      firstMonth: generateMonthData(firstMonth),
      secondMonth: generateMonthData(secondMonth),
      firstMonthName: firstMonth.format('MMMM YYYY'),
      secondMonthName: secondMonth.format('MMMM YYYY'),
    };
  }, [currentMonth]);

  const handleTimeConfirm = useCallback(() => {
    if (selectionStep === 'startTime') {
      if (allowSingleDate) {
        setSelectionStep('endTime');
      } else {
        setSelectionStep('endDate');
      }
    } else if (selectionStep === 'endTime') {
      setSelectionStep('done');
    }
  }, [selectionStep, allowSingleDate]);

  const handleDateClick = useCallback(
    (date: Dayjs) => {
      // Check min/max date constraints
      if (minDate && date.isBefore(dayjs(minDate))) return;
      if (maxDate && date.isAfter(dayjs(maxDate))) return;

      if (lockedStartDate) {
        const locked = dayjs(lockedStartDate);
        const endDay = date.isBefore(locked) ? locked : date;
        setTempStartDate(locked);
        setTempEndDate(endDay);
        goToEndStep();
        return;
      }

      if (allowSingleDate) {
        // Single date selection mode
        setTempStartDate(date);
        setTempEndDate(date);
        // Navigate to show the selected date
        setCurrentMonth(date);
        goToStartStep();
      } else if (enforceMinDays) {
        if (
          selectionStep === 'startDate' ||
          selectionStep === 'done' ||
          !tempStartDate
        ) {
          // Start new selection
          setTempStartDate(date);
          setTempEndDate(null);
          // Navigate to show the start date
          setCurrentMonth(date);
          goToStartStep();
        } else if (selectionStep === 'endDate' && tempStartDate) {
          // Complete the selection - check if date is after minimum days
          // minDays = 2 means end date should be at least 2 days after start date
          // So if start is Day 1, minimum end is Day 3 (Day 1 + 2 days)
          const minimumEndDate = tempStartDate.add(minDays, 'day');

          // Only allow dates on or after minimum end date
          if (
            date.isAfter(minimumEndDate) ||
            date.isSame(minimumEndDate, 'day')
          ) {
            setTempEndDate(date);
            // Navigate to show both start and end dates
            // If end date is in a different month, navigate to show start date month
            if (!tempStartDate.isSame(date, 'month')) {
              setCurrentMonth(tempStartDate);
            }
            goToEndStep();
          }
          // If user clicks before minimum end date, ignore the click completely
        }
      } else {
        // Range selection mode
        // Editing an already selected end date
        if (selectionStep === 'endDate' && tempStartDate && tempEndDate) {
          if (date.isBefore(tempStartDate)) {
            return;
          }

          setTempEndDate(date);

          if (!tempStartDate.isSame(date, 'month')) {
            setCurrentMonth(tempStartDate);
          }

          goToEndStep();

          return;
        }

        if (
          selectionStep === 'startDate' ||
          selectionStep === 'done' ||
          !tempStartDate
        ) {
          // Start new selection
          setTempStartDate(date);
          setTempEndDate(null);
          // Navigate to show the start date
          setCurrentMonth(date);
          goToStartStep();
        } else if (selectionStep === 'endDate' && tempStartDate) {
          // Complete the selection
          if (date.isBefore(tempStartDate)) {
            setTempEndDate(null);
            setTempStartDate(date);
            // Navigate to show the new start date
            setCurrentMonth(date);
            goToStartStep();
          } else {
            setTempEndDate(date);
            // Navigate to show both start and end dates
            // If end date is in a different month, navigate to show start date month
            if (!tempStartDate.isSame(date, 'month')) {
              setCurrentMonth(tempStartDate);
            }
            goToEndStep();
          }
        }
      }
    },
    [
      tempStartDate,
      allowSingleDate,
      minDate,
      maxDate,
      enforceMinDays,
      minDays,
      lockedStartDate,
      selectionStep,
      goToStartStep,
      goToEndStep,
    ]
  );

  const isDateInRange = useCallback(
    (date: Dayjs) => {
      if (!tempStartDate || !tempEndDate) return false;
      return (
        date.isAfter(tempStartDate.subtract(1, 'day')) &&
        date.isBefore(tempEndDate.add(1, 'day'))
      );
    },
    [tempStartDate, tempEndDate]
  );

  const isDateSelected = useCallback(
    (date: Dayjs) => {
      return (
        (tempStartDate && date.isSame(tempStartDate, 'day')) ||
        (tempEndDate && date.isSame(tempEndDate, 'day'))
      );
    },
    [tempStartDate, tempEndDate]
  );

  const isStartDateSelected = useCallback(
    (date: Dayjs) => {
      return tempStartDate && date.isSame(tempStartDate, 'day');
    },
    [tempStartDate]
  );

  const navigateMonth = useCallback((direction: 'prev' | 'next') => {
    setCurrentMonth(prev =>
      direction === 'prev' ? prev.subtract(1, 'month') : prev.add(1, 'month')
    );
  }, []);

  // Sync temp state when props change (e.g., when preset buttons are clicked in Home.tsx)
  useEffect(() => {
    if (open) {
      setTempStartDate(startDate ? dayjs(startDate) : null);
      setTempEndDate(endDate ? dayjs(endDate) : null);
      if (startDate) {
        setCurrentMonth(dayjs(startDate));
      }
    }
  }, [startDate, endDate, open]);

  // Auto-navigate to show both dates when they span across months
  useEffect(() => {
    if (tempStartDate && tempEndDate && open) {
      // If dates are in different months, show the start date month
      if (!tempStartDate.isSame(tempEndDate, 'month')) {
        setCurrentMonth(tempStartDate);
      }
    }
  }, [tempStartDate, tempEndDate, open]);

  const renderCalendar = useCallback(
    (monthData: any[], monthName: string) => {
      return (
        <Box sx={{ flex: 1, minWidth: 160 }}>
          {monthName && (
            <Typography
              variant="subtitle2"
              sx={{
                textAlign: 'center',
                mb: 0.5,
                fontWeight: 600,
                fontSize: '0.8rem',
              }}
            >
              {monthName}
            </Typography>
          )}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 0.25,
            }}
          >
            {/* Day headers */}
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
              <Box
                key={`day-header-${index}`}
                sx={{
                  p: 0.5,
                  textAlign: 'center',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                }}
              >
                {day}
              </Box>
            ))}

            {/* Calendar days */}
            {monthData.map((day, index) => {
              const isInRange = isDateInRange(day.date);
              const isSelected = isDateSelected(day.date);
              const isStartSelected = isStartDateSelected(day.date);
              const isCurrentMonth = day.isCurrentMonth;
              const isToday = day.isToday;

              // Check if date is disabled due to min/max constraints
              const isDisabledByConstraints =
                (minDate && day.date.isBefore(dayjs(minDate))) ||
                (maxDate && day.date.isAfter(dayjs(maxDate)));

              const isNotAvailable =
                availableDates &&
                availableDates.length > 0 &&
                !availableDates.some(d => dayjs(d).isSame(day.date, 'day'));

              // Check if date is disabled due to minimum days enforcement
              let isDisabledByMinDays = false;
              if (enforceMinDays && tempStartDate && !tempEndDate) {
                // minDays = 2 means end date should be at least 2 days after start date
                // So if start is Day 1, minimum end is Day 3 (Day 1 + 2 days)
                const minimumEndDate = tempStartDate.add(minDays, 'day');
                // Disable dates between start date (exclusive) and minimum end date (exclusive)
                isDisabledByMinDays =
                  day.date.isAfter(tempStartDate) &&
                  day.date.isBefore(minimumEndDate);
                // Don't disable the start date itself - it should remain clickable for visual feedback
              }

              const isDisabled =
                isDisabledByConstraints ||
                isDisabledByMinDays ||
                isNotAvailable;

              return (
                <Box
                  key={index}
                  onClick={() => !isDisabled && handleDateClick(day.date)}
                  sx={{
                    p: 0.5,
                    textAlign: 'center',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    borderRadius: 0.5,
                    fontSize: '0.75rem',
                    fontWeight: isToday ? 600 : 400,
                    minHeight: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isDisabled
                      ? theme.palette.text.disabled
                      : isCurrentMonth
                        ? isSelected || (isStartSelected && !tempEndDate)
                          ? theme.palette.primary.contrastText
                          : theme.palette.text.primary
                        : theme.palette.text.disabled,
                    backgroundColor: isDisabled
                      ? 'transparent'
                      : isSelected
                        ? theme.palette.primary.main
                        : isStartSelected && !tempEndDate
                          ? theme.palette.primary.main
                          : isInRange
                            ? alpha(theme.palette.primary.main, 0.1)
                            : 'transparent',
                    opacity: isDisabled ? 0.4 : 1,
                    '&:hover': {
                      backgroundColor: isDisabled
                        ? 'transparent'
                        : isSelected
                          ? theme.palette.primary.dark
                          : isStartSelected && !tempEndDate
                            ? theme.palette.primary.dark
                            : alpha(theme.palette.primary.main, 0.2),
                    },
                    border:
                      isToday && showTodayHighlight
                        ? `1px solid ${theme.palette.primary.main}`
                        : 'none',
                  }}
                >
                  {day.date.date()}
                </Box>
              );
            })}
          </Box>
        </Box>
      );
    },
    [
      theme,
      isDateInRange,
      isDateSelected,
      isStartDateSelected,
      handleDateClick,
      tempEndDate,
      enforceMinDays,
      tempStartDate,
      minDays,
      minDate,
      maxDate,
      showTodayHighlight,
      availableDates,
    ]
  );

  return (
    <Box>
      <TextField
        fullWidth={fullWidth}
        label={label}
        placeholder={placeholder}
        value={formatDateRange()}
        onClick={handleClick}
        variant={variant}
        size={size}
        required={required}
        className={className}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                handleClick(e as any);
              }}
              disabled={disabled}
              sx={{ color: theme.palette.primary.main }}
            >
              <CalendarIcon />
            </IconButton>
          ),
        }}
        error={error}
        helperText={helperText}
        disabled={disabled}
        sx={{
          '& .MuiOutlinedInput-root': {
            cursor: disabled ? 'default' : 'pointer',
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            },
          },
          ...sx,
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={
          popoverProps.anchorOrigin || {
            vertical: 'bottom',
            horizontal: 'right',
          }
        }
        transformOrigin={
          popoverProps.transformOrigin || {
            vertical: 'top',
            horizontal: 'right',
          }
        }
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 2,
            boxShadow: theme.shadows[8],
            minWidth: popoverProps.minWidth || 400,
            maxWidth: popoverProps.maxWidth || 500,
          },
        }}
      >
        <Paper sx={{ p: 2 }}>
          {/* Progress Indicator */}
          {includeTime && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                borderRadius: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                Selection Progress
              </Typography>

              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}
              >
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Start
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: tempStartDate ? 600 : 400 }}
                  >
                    {tempStartDate
                      ? tempStartDate.format('MMM DD, YYYY')
                      : 'Select Date'}
                    {tempStartDate &&
                    (selectionStep === 'endDate' ||
                      selectionStep === 'endTime' ||
                      selectionStep === 'done')
                      ? ` ${dayjs(`2000-01-01 ${tempStartTime}`).format('hh:mm A')}`
                      : ''}
                  </Typography>
                </Box>

                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="textSecondary">
                    End
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: tempEndDate ? 600 : 400 }}
                  >
                    {tempEndDate
                      ? tempEndDate.format('MMM DD, YYYY')
                      : selectionStep === 'endDate'
                        ? 'Select Date'
                        : '--'}
                    {tempEndDate && selectionStep === 'done'
                      ? ` ${dayjs(`2000-01-01 ${tempEndTime}`).format('hh:mm A')}`
                      : ''}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Header with navigation */}
          {showCalendar && showNavigation && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1.5,
              }}
            >
              <IconButton
                size="small"
                onClick={() => navigateMonth('prev')}
                sx={{ color: theme.palette.primary.main }}
              >
                <NavigateBeforeIcon />
              </IconButton>

              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, fontSize: '0.9rem' }}
              >
                {singleMonth
                  ? calendarData.firstMonthName
                  : `${calendarData.firstMonthName} - ${calendarData.secondMonthName}`}
              </Typography>

              <IconButton
                size="small"
                onClick={() => navigateMonth('next')}
                sx={{ color: theme.palette.primary.main }}
              >
                <NavigateNextIcon />
              </IconButton>
            </Box>
          )}

          {/* Calendar grids or Time Picker */}
          {showCalendar ? (
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
              {renderCalendar(
                calendarData.firstMonth,
                singleMonth ? '' : calendarData.firstMonthName
              )}
              {!singleMonth &&
                renderCalendar(
                  calendarData.secondMonth,
                  calendarData.secondMonthName
                )}
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: 4,
                mb: 2,
                minWidth: 300,
              }}
            >
              <Typography variant="h6" gutterBottom>
                {selectionStep === 'startTime'
                  ? 'Edit Start Time'
                  : 'Edit End Time'}
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <TimePicker
                  label={
                    selectionStep === 'startTime' ? 'Start Time' : 'End Time'
                  }
                  value={dayjs(
                    `2025-01-01 ${
                      selectionStep === 'startTime'
                        ? tempStartTime
                        : tempEndTime
                    }`
                  )}
                  onChange={value => {
                    if (!value) return;

                    if (selectionStep === 'startTime') {
                      setTempStartTime(value.format('HH:mm'));
                    } else {
                      setTempEndTime(value.format('HH:mm'));
                    }
                  }}
                />
              </LocalizationProvider>
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    if (selectionStep === 'startTime') {
                      setSelectionStep('startDate');
                    } else if (selectionStep === 'endTime') {
                      setSelectionStep('endDate');
                    }
                  }}
                >
                  Back
                </Button>
                <Button variant="contained" onClick={handleTimeConfirm}>
                  Next
                </Button>
              </Box>
            </Box>
          )}

          {/* Helper text for minimum days */}
          {showCalendar && enforceMinDays && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                textAlign: 'center',
                color: theme.palette.text.secondary,
                mb: 1,
                fontStyle: 'italic',
              }}
            >
              Minimum {minDays} days required
            </Typography>
          )}

          {/* Action buttons — Cancel on the left, Apply on the right */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: 1.5,
            }}
          >
            <Button onClick={handleClose} variant="outlined" size="small">
              Cancel
            </Button>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={handleApply}
                variant="contained"
                size="small"
                disabled={isApplyDisabled()}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </Paper>
      </Popover>
    </Box>
  );
};

export { DateRangePicker };
export default DateRangePicker;
